/**
 * Fixed-size ring buffer of per-frame timing and camera samples, used by the
 * browser validation tooling to measure frame pacing, catch-up behaviour and
 * interpolation smoothness. Dev builds only; costs one object write per frame.
 */
export interface FrameSample {
  t: number;
  frameMs: number;
  ticks: number;
  alpha: number;
  eyeX: number;
  eyeY: number;
  eyeZ: number;
  yaw: number;
  speed: number;
}

export class FrameTrace {
  private readonly buf: FrameSample[];
  private next = 0;
  private count = 0;

  constructor(readonly capacity: number) {
    this.buf = Array.from({ length: capacity }, () => ({ t: 0, frameMs: 0, ticks: 0, alpha: 0, eyeX: 0, eyeY: 0, eyeZ: 0, yaw: 0, speed: 0 }));
  }

  push(s: Readonly<FrameSample>): void {
    Object.assign(this.buf[this.next]!, s);
    this.next = (this.next + 1) % this.capacity;
    this.count = Math.min(this.count + 1, this.capacity);
  }

  /** Oldest first, as plain copies. */
  samples(): FrameSample[] {
    const out: FrameSample[] = [];
    const start = (this.next - this.count + this.capacity) % this.capacity;
    for (let i = 0; i < this.count; i++) out.push({ ...this.buf[(start + i) % this.capacity]! });
    return out;
  }

  clear(): void {
    this.next = 0;
    this.count = 0;
  }
}
